import { AxiosResponse } from 'axios';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { DownloadProgress, downloadStreamedCsv } from './streaming-download';

/**
 * Time-bucketed OPC-N3 bin matrix for one device.
 * `values[i][b]` = average concentration of bin b during the bucket starting at
 * `timestamps[i]`; null = the device reported nothing there (gap, not zero).
 */
export type OpcBinHistogramResponse = {
  serial: number;
  startDate: number;
  endDate: number;
  interval: string;
  intervalMillis: number;
  binCount: number;
  timestamps: number[];
  values: (number | null)[][];
};

/** Box-plot statistics for one OPC-N3 bin over the selected window. */
export type OpcBinStats = {
  bin: number;
  count: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  p05: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p95: number | null;
};

export type OpcBinDistributionResponse = {
  serial: number;
  startDate: number;
  endDate: number;
  sampleCount: number;
  binCount: number;
  bins: OpcBinStats[];
};

const BASE = `${DEV_NEW_DOMAIN_URL}/opc-bins`;

/**
 * Fetch the time x bin concentration matrix (heat map / contour / bar charts).
 * @param serial - Device serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @param interval - Optional fixed interval like "5m"; server auto-picks when omitted.
 * @returns Axios response with the histogram matrix.
 */
export const getOpcBinHistogram = (
  serial: number,
  startDate: number,
  endDate: number,
  interval?: string
): Promise<AxiosResponse<OpcBinHistogramResponse>> => {
  return axiosInstance.get(`${BASE}/histogram`, {
    params: { serial, startDate, endDate, ...(interval ? { interval } : {}) }
  });
};

/**
 * Fetch per-bin distribution statistics (size-distribution box plot).
 * @param serial - Device serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @returns Axios response with per-bin stats.
 */
export const getOpcBinDistribution = (
  serial: number,
  startDate: number,
  endDate: number
): Promise<AxiosResponse<OpcBinDistributionResponse>> => {
  return axiosInstance.get(`${BASE}/distribution`, {
    params: { serial, startDate, endDate }
  });
};

/**
 * Filename mirroring the one the export endpoint sets, computed client-side so
 * the save dialog can be opened before the request starts (see below).
 * @param serial - Device serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @returns Suggested filename.
 */
function exportFilename(serial: number, startDate: number, endDate: number): string {
  /**
   * Date part of a timestamp, for the filename.
   * @param ms - Epoch millis.
   * @returns "YYYY-MM-DD".
   */
  const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
  return `opc-bins-${serial}-${day(startDate)}-to-${day(endDate)}.csv`;
}

/**
 * Download every raw sample in the window as CSV.
 *
 * Not bucketed like the charts — this is the full-resolution data, streamed by
 * the server and written straight to disk. See {@link downloadStreamedCsv}.
 * @param serial - Device serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @param onProgress - Optional callback receiving bytes downloaded so far.
 * @returns Resolves once the file is written, or immediately if the user cancels.
 */
export const downloadOpcBinCsv = async (
  serial: number,
  startDate: number,
  endDate: number,
  onProgress?: DownloadProgress
): Promise<void> => {
  const params = new URLSearchParams({
    serial: String(serial),
    startDate: String(startDate),
    endDate: String(endDate)
  });
  await downloadStreamedCsv(
    `${BASE}/export?${params}`,
    exportFilename(serial, startDate, endDate),
    onProgress
  );
};
